// =============================================================================
// domain/journalHidden.ts — which Journal sections a trader has hidden.
// -----------------------------------------------------------------------------
// Josh, 2026-10-06 (TCR-81): *"I'd like to see a little gear icon that allows the
// user to show/hide from a list rather than have to click a button within each
// element and then go and find the section at the bottom of the page to restore
// them, if needed."*
//
// So beta's two halves — a ⊘ button inside every hideable card, and a restore
// tray at the foot of the page — are replaced by ONE list. This module holds the
// part that is pure: the curated set, the stored keys, and the union rule.
//
// ── 🔴 THE UNION RULE IS THE WHOLE REASON THIS IS IN `domain/` ──
//
// Beta renders its restore tray from `hidden.filter(k => JP_HIDEABLE[k])` — so a
// stored key its curated set does not recognise is hidden with NO WAY BACK.
//
// ⚠ THAT IS LIVE ON JOSH'S OWN ROW, not hypothetical. His 2026-08-08 export
// carries `journalHiddenFields: ['mood']`, and `mood` is in neither beta's
// `JP_HIDEABLE` nor any `data-jp-hideable` attribute in its markup — measured
// 2026-10-06. It hides nothing and beta cannot list it. It is an orphan no
// control can clear.
//
// `rows()` below therefore offers the curated set PLUS whatever else is stored,
// exactly as `ui/tabs/SidebarVisibility.tsx` already does for hidden tabs, whose
// header names the same hazard: *"hiding without listing strands the tab with no
// way to bring it back."*
//
// ── ⚠ HIDING IS A VIEW CONCERN. IT MUST NEVER TOUCH THE DATA ──
//
// Nothing here removes a field from a plan or a recap. The screens render hidden
// units with CSS and keep the inputs mounted — beta's reason was that its form
// reader still needs them, and ours is sharper since TCR-79: both editors now
// autosave on blur against a JSON diff of the draft, so a hide that dropped
// fields would make the next autosave PERSIST the deletion as a real edit.
// =============================================================================

/** The key inside `app_flags`. Beta's name, because both clients write it. */
export const JOURNAL_HIDDEN_FLAG = 'journalHiddenFields';

/** Which Journal phase a unit belongs to. `null` = a stray we do not recognise. */
export type HidePhase = 'pre' | 'recap';

export interface HideableUnit {
  /** The stored key. Beta's exact string where beta has one. */
  id: string;
  label: string;
  /** Shown under the label in the gear list. */
  description: string;
  /**
   * The phase whose drawer offers it (TCR-82).
   *
   * 🔴 ONE DRAWER PER PHASE, on Josh's instruction after seeing a single mixed
   * list: *"I think there should be a separate drawer for each tab. I don't think
   * one drawer should fit all."* Nothing says which entry belongs to which screen
   * when they share a list, so the phase is data on the unit rather than a
   * guess made by the component.
   */
  phase: HidePhase;
  /**
   * The section this sits inside, if any — rendered indented beneath it.
   *
   * ⚠ BETA OFFERS THE THREE MARKET READ CARDS BUT NOT THE SECTION AROUND THEM,
   * and Josh asked for both ("Nest the three under it"). That makes a state
   * reachable where the parent is hidden and a child is still ticked. The parent
   * wins on screen — it is the thing being hidden — but the child's STORED key is
   * never rewritten, because those keys are beta's and beta is still writing
   * them. Silently clearing one here would hide a change from the other client.
   */
  parent?: string;
}

/**
 * Pre-Market's six — TRANSCRIBED from beta's `JP_HIDEABLE`, not chosen.
 *
 * ⚠ THE KEY STRINGS ARE BETA'S, INCLUDING THE `sec:` PREFIX. Both clients write
 * the same column, so renaming one here would silently strand whatever the other
 * client hid under the old name — the orphan problem, manufactured by us.
 */
export const PRE_HIDEABLE: readonly HideableUnit[] = [
  // ── The five sections `PlanEditor` actually renders, in screen order ──
  //
  // 🔴 TWO OF THESE HAD NO KEY AT ALL UNTIL TCR-82, which is why Josh could not
  // find them in the list: *"I also don't think that everything in the pre-market
  // plan section is included."* `Market Read` and `Setups Hunting` were simply
  // never wired. Measured against `PlanEditor.tsx`, which renders exactly five
  // `<Sec>` elements — if a sixth is ever added it belongs here too.
  {
    id: 'pre:marketRead', label: 'Market Read',
    description: 'The three regime cards below. Hiding this hides all of them.',
    phase: 'pre',
  },
  // ⚠ BETA'S KEYS, AND THEY KEEP BETA'S EXACT STRINGS — both clients write this
  // column, so renaming one strands whatever the other hid under the old name.
  // They are children of the section above on screen, but their stored keys are
  // flat and independent, exactly as beta writes them.
  {
    id: 'marketRegime', label: 'Market Regime', parent: 'pre:marketRead',
    description: 'The regime pick and its notes. Hide it if you read structure elsewhere.',
    phase: 'pre',
  },
  {
    id: 'gammaRegime', label: 'Gamma Regime', parent: 'pre:marketRead',
    description: 'The gamma pick and its notes. Hide it if you do not trade options flow.',
    phase: 'pre',
  },
  {
    id: 'overallBias', label: 'Overall Bias', parent: 'pre:marketRead',
    description: 'Your directional lean for the day, and the reasoning under it.',
    phase: 'pre',
  },
  {
    id: 'sec:screenshots', label: 'Chart Screenshots',
    description: 'Images attached to the plan. Hiding does not delete anything already attached.',
    phase: 'pre',
  },
  {
    id: 'sec:news', label: 'News & Events',
    description: 'Scheduled events and how you will play them.',
    phase: 'pre',
  },
  {
    id: 'pre:setupsHunting', label: 'Setups Hunting',
    description: 'Your hypotheses for the day, with the patterns and disqualifier on each.',
    phase: 'pre',
  },
  {
    id: 'sec:selfassessment', label: 'Self-Assessment',
    description: 'Mood, focus, energy, stress, sleep and caffeine.',
    phase: 'pre',
  },
];

/**
 * The Recap's six — added on Josh's instruction 2026-10-06.
 *
 * 🔴 BETA OFFERS NONE OF THESE, so there is nothing to transcribe and these keys
 * are ours. They carry a `recap:` prefix for two reasons: it cannot collide with
 * a Pre-Market field name, and it leaves beta free to add its own keys later
 * without meeting ours. ▶ ANY FUTURE PHASE FOLLOWS THE SAME SHAPE — `<phase>:`
 * for a field, and beta's bare `sec:` only where beta already chose it.
 */
export const RECAP_HIDEABLE: readonly HideableUnit[] = [
  // ── The three sections `RecapPanel` renders, with the EOD fields nested ──
  //
  // ⚠ ADDED BY TCR-82 for the same completeness Josh applied to Pre-Market: the
  // drawer offers everything that phase actually puts on screen, not only the
  // fields inside one of its sections.
  {
    id: 'recap:sec:eod', label: 'EOD Recap',
    description: 'The six review boxes below. Hiding this hides all of them.',
    phase: 'recap',
  },
  {
    id: 'recap:whatWorked', label: 'Successful setups', phase: 'recap', parent: 'recap:sec:eod',
    description: 'What played out as planned.',
  },
  {
    id: 'recap:whatBroke', label: 'Failed setups', phase: 'recap', parent: 'recap:sec:eod',
    description: 'Valid setups that did not play out.',
  },
  {
    id: 'recap:wins', label: 'Things I did well', phase: 'recap', parent: 'recap:sec:eod',
    description: 'The execution half of the review, not the outcome half.',
  },
  {
    id: 'recap:mistakes', label: 'Things I need to fix', phase: 'recap', parent: 'recap:sec:eod',
    description: 'The paired opposite of “did well”. Hiding one keeps the other in its own column.',
  },
  {
    id: 'recap:lessons', label: 'Lessons', phase: 'recap', parent: 'recap:sec:eod',
    description: 'What you take forward from the session.',
  },
  {
    // ⚠ THE WARNING IS LOAD-BEARING, NOT A COURTESY. This is the one box that
    // leaves the screen: it is carried onto the next day's Pre-Market as
    // "Today's Focus". Hide it and a trader simply never writes it, so the carry
    // stops — quietly, and with nothing on either screen to explain why.
    id: 'recap:tomorrowFocus', label: 'Tomorrow’s focus', phase: 'recap', parent: 'recap:sec:eod',
    description: 'Carried onto tomorrow’s Pre-Market as “Today’s Focus” — hide it and that stops.',
  },
  {
    id: 'recap:sec:state', label: 'State',
    description: 'How you felt through the session, recorded after the fact.',
    phase: 'recap',
  },
  {
    id: 'recap:sec:voice', label: 'Voice Notes',
    description: 'Recordings attached to the day. Hiding does not delete anything recorded.',
    phase: 'recap',
  },
];

/** Everything this client knows how to hide, in the order the gear lists it. */
export const JOURNAL_HIDEABLE: readonly HideableUnit[] = [
  ...PRE_HIDEABLE, ...RECAP_HIDEABLE,
];

/**
 * The stored keys.
 *
 * ⚠ TOLERANT OF A MALFORMED BLOB. `app_flags` is written by two clients and by
 * hand during support; a string or an object where an array belongs must read as
 * "nothing hidden" rather than throw and blank the tab.
 */
export function readJournalHidden(
  appFlags: Record<string, unknown> | null | undefined,
): readonly string[] {
  const raw = (appFlags ?? {})[JOURNAL_HIDDEN_FLAG];
  if (!Array.isArray(raw)) return [];
  return raw.filter((k): k is string => typeof k === 'string' && k !== '');
}

/**
 * The whole flags blob with the hidden list replaced.
 *
 * 🚨 SPREAD, NOT REPLACE — the same rule as `withPnlUnit`, and the same stakes.
 * `app_flags` is one JSONB column shared by every surface in BOTH clients;
 * sixteen keys on Josh's row. Returning `{ journalHiddenFields: next }` would
 * type-check, save cleanly, and delete his leak rules, his display unit, his
 * journal layout and his custom widgets. The caller must pass the blob it READ.
 */
export function withJournalHidden(
  appFlags: Record<string, unknown> | null | undefined,
  hidden: readonly string[],
): Record<string, unknown> {
  // ⚠ Deduped and ordered so two ticks cannot write the same key twice and a
  // save is stable to compare against what is already stored.
  const clean = [...new Set(hidden.filter((k) => k !== ''))].sort();
  return { ...(appFlags ?? {}), [JOURNAL_HIDDEN_FLAG]: clean };
}

/**
 * What the gear list offers: the curated set, plus anything stored that it does
 * not recognise.
 *
 * 🔴 THE SECOND HALF IS THE POINT. Without it an unknown key is hidden forever —
 * beta's defect, and `mood` is sitting in Josh's row proving it. A stray renders
 * under its own id so it can be seen and unticked away.
 */
export function journalHiddenRows(
  hidden: readonly string[],
  phase: HidePhase,
): readonly HideableUnit[] {
  const known = new Set(JOURNAL_HIDEABLE.map((u) => u.id));
  /**
   * 🔴 A STRAY IS OFFERED IN EVERY PHASE'S DRAWER, and that is a decision with a
   * reason rather than a convenience.
   *
   * A stored key we do not recognise belongs to no phase by definition — nothing
   * on any screen claims it. Putting it in one drawer would mean a trader who
   * never opens that phase can never clear it, which is the beta defect this rule
   * exists to fix, merely narrowed. Showing it in each is mildly redundant and
   * always recoverable, and redundant beats stranded.
   *
   * ⚠ It is listed LAST and says plainly that no screen here owns it, so it does
   * not read as part of the phase it appears in.
   */
  const strays = readJournalHidden({ [JOURNAL_HIDDEN_FLAG]: hidden })
    .filter((id) => !known.has(id))
    .map((id) => ({
      id,
      label: id,
      description:
        'Hidden, and this client has no such section — untick to clear the setting.',
      phase,
    }));
  return [...JOURNAL_HIDEABLE.filter((u) => u.phase === phase), ...strays];
}

/** Is this unit hidden? */
export function isHidden(hidden: readonly string[], id: string): boolean {
  return hidden.includes(id);
}

/** The list with one unit shown or hidden. Pure; does not write. */
export function toggleHidden(
  hidden: readonly string[], id: string, visible: boolean,
): readonly string[] {
  return visible
    ? hidden.filter((k) => k !== id)
    : [...new Set([...hidden, id])];
}
